import {
  QUESTION_SWITCH,
  rowsFor,
  setsUp,
  userKind,
  type Answers,
  type FineTune,
  type Preset,
  type ScopeAnswer,
} from "../../../server/rules/jobs";
import {
  AREA_LABEL,
  AREAS,
  effectiveAccess,
  SWITCH_AREA,
  SWITCH_LABEL,
  type Area,
  type Switch,
} from "../../../server/rules/permissions";
import type { ScreenContext } from "../../shell/types";
import { activityPanel } from "../../ui/activity";
import { api, el, toast, together } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { chip, ib, table } from "../../ui/record";
import { field, refuse } from "../clients/forms";
import { pageHead } from "../empty";
import { jobsCards, type JobRow, type JobsBody } from "./jobs";
import { warningBox } from "../../shell/idle";
import { countdown, warnWords } from "../../../server/rules/sign-in";

// Settings › Users and roles (mockup v50, brief 1D-5): Office users and Field users · free, worked
// out from what each person does; the people table (Person, Does, Sees, Pay and billing, Signs in
// with, Email goes out, Last active, the pencil); Add someone and the pencil open the questions
// sheet (What do they do?, three yes or no questions, which patients and clients with the county
// or client chips, "This sets up", office or field user, Fine-tune with Off, View or Edit per area
// and the switches; "Something else" names a new job); the Starting simple card; and the roles
// with their levels and switches (Jobs and what they start with; the Admin row is locked). Brief
// 1E-7 (mockup v59) adds the Starter roles, Jobs and More than one job cards (./jobs.ts), and on
// the person sheet their jobs, extras and the account owner. The rules decide, on the server;
// every change is in the history.

interface Perm {
  area: Area;
  level: "none" | "view" | "edit";
  own_only: boolean;
  switches: Partial<Record<Switch, boolean>>;
}
interface Role {
  id: string;
  name: string;
  builtIn: boolean;
  fixed: boolean;
  people: number;
  permissions: Perm[];
}
/** The mockup's "Sees" column, worked out from the role's rows. */
function sees(role: Role): string {
  if (role.fixed) return "Everything";
  const part = (level: "view" | "edit") =>
    AREAS.flatMap((a) => role.permissions.filter((p) => p.area === a))
      .filter((p) => p.level === level)
      .map((p) => `${AREA_LABEL[p.area]}${p.own_only ? " (own)" : ""}`);
  const edit = part("edit");
  const view = part("view");
  const switches = role.permissions.flatMap((p) =>
    Object.entries(p.switches)
      .filter(([, on]) => on)
      .map(([s]) => SWITCH_LABEL[s as Switch].toLowerCase()),
  );
  const out = [
    edit.length ? `Edit: ${edit.join(", ")}` : "",
    view.length ? `View: ${view.join(", ")}` : "",
    switches.length ? `Can ${switches.join(", ")}` : "",
  ].filter(Boolean);
  return out.length ? out.join(" · ") : "Nothing yet";
}

const REFUSED: Record<number, string> = {
  403: "Only access the giver has can be given, and Admin can't be given or taken.",
};

type Spec = "off" | "view" | "edit" | "view own" | "edit own";
interface Setup {
  presetId: string | null;
  answers: Answers;
  grid: Record<string, Spec>;
  switches: Record<string, boolean>;
  scopes: { kind: string; value: string }[];
}
interface Person {
  id: string;
  name: string;
  email: string;
  status: "active" | "off";
  roleIds: string[];
  does: string;
  sees: string;
  money: boolean;
  kind: "office" | "field" | "none";
  signsIn: string;
  emailOut: string;
  lastActive: string;
  admin: boolean;
  setup: Setup;
  /** Brief 1E-5: their professional title (null: none, office only). */
  title: string | null;
  /** Brief 1E-7: each job they hold (more than one allowed), each one-off extra, owner or not. */
  jobs: { roleId: string; name: string; presetId: string | null; added: boolean }[];
  extras: string[];
  owner: boolean;
}
interface Job {
  id: string;
  name: string;
  field: boolean;
  answers: Answers;
  grid: Record<string, Spec>;
  switches: Record<string, boolean>;
}
interface UsersBody {
  users: Person[];
  counts: { office: number; field: number };
  presets: Job[];
  questions: { key: "money" | "payroll" | "release"; words: string }[];
  scopeAnswers: { value: string; label: string }[];
  areas: { value: Area; label: string }[];
  switches: { value: Switch; label: string }[];
  counties: string[];
  clients: { id: string; name: string }[];
  clientTypes: { id: string; name: string }[];
  hints: { on: boolean; suggestAdmin: boolean; days: number };
  canAdmin: boolean;
  preset?: { id: string; name: string };
  /** The Professional title row's choices (null first: none, office only), and who may set it. */
  titles: (string | null)[];
  canSetTitle: boolean;
  /** How long View as a job lasts (the company's setting, brief 1E-6). */
  viewAsMinutes: number;
}

const presetOf = (j: Job): Preset => ({
  id: j.id,
  name: j.name,
  // The grid's levels are the preset's areas ("view", "edit own", ...); off is left out.
  areas: Object.fromEntries(Object.entries(j.grid).filter(([, v]) => v !== "off")),
  switches: Object.entries(j.switches)
    .filter(([, on]) => on)
    .map(([k]) => k),
  scope: j.answers.scope as ScopeAnswer,
  field: j.field,
});

export async function usersScreen(host: HTMLElement, ctx: ScreenContext) {
  const add = el(
    "button",
    { type: "button", class: "btn pri" },
    icon("person-add"),
    " Add someone",
  );
  host.append(
    pageHead(
      ctx,
      "Who can sign in, what each person sees and does, and how their email goes out.",
      [add],
      "Company settings",
    ),
  );
  const body = el("div", { class: "wrap", style: "gap:18px" });
  host.append(body);
  let u: UsersBody;
  let roles: Role[] = [];
  let jobsBody: JobsBody | null = null;
  // Brief 1E-15 (mockup v71): Signing in (Admin): sign out when idle.
  let signing: {
    officeMinutes: number;
    warnSeconds: number;
    fieldMinutes: number;
    choices: { warn: number[] };
    minutes: { min: number; max: number };
  } | null = null;
  // Ready once the people and the jobs are in.
  add.disabled = true;
  add.addEventListener("click", () => questions(null));

  /** Starts viewing as a job from the Jobs table (brief 1E-7): read-only, for the time limit. */
  async function startViewAs(r: JobRow) {
    const res = await api<{ viewAs: { job: string; minutesLeft: number } }>(
      "POST",
      "/api/view-as",
      { presetId: r.id },
    );
    if (!res.ok) return toast(`Couldn't view as ${r.name}`, res.body.error);
    toast(
      `Viewing as ${res.body.viewAs.job}`,
      `Read-only for ${res.body.viewAs.minutesLeft} minutes; recorded in the audit log`,
    );
    location.assign("/");
  }

  async function load() {
    // One call for the screen (brief 1E-29); an Admin's Signing in comes with it.
    const [a, r, j, si] = await together(() =>
      Promise.all([
        api<UsersBody>("GET", "/api/settings/users"),
        api<{ roles: (Role & { personal?: boolean })[] }>("GET", "/api/settings/roles"),
        api<JobsBody>("GET", "/api/settings/jobs"),
        ctx.me.admin
          ? api<NonNullable<typeof signing>>("GET", "/api/settings/signing-in")
          : Promise.resolve(null),
      ]),
    );
    if (!a.ok || !r.ok || !j.ok) {
      body.replaceChildren(
        el("p", { class: "note", role: "alert" }, "Users and roles couldn't be loaded."),
      );
      return;
    }
    u = a.body;
    roles = r.body.roles.filter((x) => !x.personal);
    jobsBody = j.body;
    signing = u.canAdmin && si?.ok ? si.body : null;
    add.disabled = false;
    draw();
  }

  /** Signing in (mockup v71): the office app's and field app's idle minutes, the warning. */
  function signingCard(): HTMLElement | null {
    if (!signing) return null;
    const s = signing;
    const pick = (
      id: string,
      key: "warnSeconds",
      choices: number[],
      words: (n: number) => string,
      saved: (n: number) => string,
    ) => {
      const sel = el(
        "select",
        { id },
        ...choices.map((n) => el("option", { value: String(n), selected: n === s[key] }, words(n))),
      );
      sel.addEventListener("change", async () => {
        sel.disabled = true;
        const r = await api<typeof s>("PUT", "/api/settings/signing-in", {
          [key]: Number(sel.value),
        });
        sel.disabled = false;
        if (!r.ok) {
          sel.value = String(s[key]);
          return toast("That wasn't saved", r.body.error);
        }
        signing = r.body;
        toast("Saved", saved(Number(sel.value)));
        await ctx.refresh();
      });
      return sel;
    };
    // Brief 1E-30: any whole minutes typed in; 0 is no idle sign-out.
    const minutesIn = (id: string, key: "officeMinutes" | "fieldMinutes", app: string) => {
      const i = el("input", {
        id,
        type: "number",
        inputmode: "numeric",
        min: String(s.minutes.min),
        max: String(s.minutes.max),
        step: "1",
        value: String(s[key]),
        style: "max-width:8em",
      });
      i.addEventListener("change", async () => {
        i.disabled = true;
        const r = await api<typeof s>("PUT", "/api/settings/signing-in", { [key]: i.value });
        i.disabled = false;
        if (!r.ok) {
          i.value = String(s[key]);
          return toast("That wasn't saved", r.body.error);
        }
        signing = r.body;
        const n = r.body[key];
        toast(
          "Saved",
          n === 0
            ? `${app} has no idle sign-out`
            : `${app} signs out after ${n} minute${n === 1 ? "" : "s"} idle`,
        );
        await ctx.refresh();
      });
      return i;
    };
    const office = minutesIn("lk1", "officeMinutes", "Office app");
    const warn = pick(
      "lk2",
      "warnSeconds",
      s.choices.warn,
      warnWords,
      (n) => `Warning shows ${warnWords(n)} before`,
    );
    const field = minutesIn("lk3", "fieldMinutes", "Field app");
    const see = el("button", { type: "button", class: "btn sm" }, icon("eye"), " See the warning");
    see.addEventListener("click", () => {
      // A preview: nothing signs out; either button closes it.
      let left = s.warnSeconds;
      const box = warningBox({
        minutes: s.officeMinutes,
        seconds: left,
        stay: () => close("Still signed in"),
        out: () => close("Nothing was signed out"),
      });
      const tick = setInterval(() => {
        left = Math.max(0, left - 1);
        box.count.textContent = countdown(left);
        if (!left) close("That's the warning");
      }, 1000);
      function close(words: string) {
        clearInterval(tick);
        box.root.remove();
        toast(words);
      }
    });
    const f = (id: string, label: string, control: HTMLElement) =>
      el("div", { class: "field" }, el("label", { for: id }, label), control);
    return el(
      "section",
      { class: "card", "aria-label": "Signing in" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, icon("lock"), " Signing in"),
          el("span", { class: "small muted" }, "Applies to everyone in the company."),
        ),
        see,
      ),
      el(
        "div",
        { class: "grid3", style: "gap:12px" },
        f("lk1", "Office app: sign out after (minutes idle)", office),
        f("lk2", "Warn before signing out", warn),
        f("lk3", "Field app: sign out after (minutes idle)", field),
      ),
      el(
        "p",
        { class: "hint", style: "margin:6px 0 0" },
        "Minutes idle, 0 for no idle sign-out. Signing in again opens the same page, with anything typed still there.",
      ),
    );
  }

  function draw() {
    const first = (n: string) => n.split(" ")[0] ?? n;
    const people = table<Person>(
      [
        {
          h: "Person",
          f: (p) =>
            el("span", {}, el("b", {}, p.name), el("div", { class: "small muted" }, p.email)),
        },
        { h: "Does", f: (p) => p.does },
        { h: "Sees", f: (p) => p.sees },
        { h: "Pay and billing", f: (p) => (p.money ? chip("Sees", "info") : chip("No", "plain")) },
        { h: "Signs in with", f: (p) => p.signsIn },
        { h: "Email goes out", f: (p) => p.emailOut },
        { h: "Last active", f: (p) => p.lastActive },
        {
          h: "",
          f: (p) =>
            p.admin && !u.canAdmin
              ? el("span")
              : ib("pencil", `Change what ${first(p.name)} sees and does`, () => questions(p)),
        },
      ],
      u.users,
      undefined,
      "People",
    );
    const hintBox = (key: "on" | "suggestAdmin", words: string) => {
      const i = el("input", { type: "checkbox", checked: u.hints[key] });
      i.addEventListener("change", async () => {
        i.disabled = true;
        const r = await api<UsersBody>("PUT", "/api/settings/hints", { [key]: i.checked });
        i.disabled = false;
        if (!r.ok) {
          i.checked = !i.checked;
          return toast("That wasn't saved", r.body.error);
        }
        u = r.body;
        toast("Saved", words);
      });
      return el("label", { class: "ptask" }, i, ` ${words}`);
    };
    const roleRows = roles.map((r) => {
      let action: HTMLElement;
      if (r.fixed) {
        action = el(
          "span",
          {
            class: "chip plain",
            "data-tip": "Admin is fixed: it sees everything and can't be changed",
          },
          icon("lock"),
          " Locked",
        );
      } else {
        action = el(
          "button",
          { type: "button", class: "btn sm", "aria-label": `Edit ${r.name}` },
          icon("pencil"),
          " Edit",
        );
        action.addEventListener("click", () => editRole(r));
      }
      // View as moved to the Jobs table's eye (brief 1E-7).
      return el(
        "tr",
        { "data-role": r.name },
        el("td", {}, el("b", {}, r.name)),
        el("td", {}, sees(r)),
        el("td", { class: "num" }, String(r.people)),
        el("td", { class: "num" }, el("span", { class: "actions" }, action)),
      );
    });
    body.replaceChildren(
      el(
        "div",
        { class: "vksum", "aria-label": "Users counted" },
        el("span", {}, "Office users", el("b", {}, String(u.counts.office))),
        el("span", {}, "Field users", el("b", {}, `${u.counts.field} · free`)),
        el(
          "span",
          { class: "muted" },
          "Office users are counted on the plan; caregivers, drivers and workers on the phone app are free.",
        ),
      ),
      el("section", { "aria-label": "People" }, people),
      el(
        "section",
        { class: "card", "aria-label": "Starting simple" },
        el(
          "div",
          { class: "cardhead" },
          el(
            "div",
            {},
            el("h2", {}, icon("light-bulb"), " Starting simple"),
            el(
              "span",
              { class: "small muted" },
              "New people see only what their job needs on day one; the rest shows up when it becomes useful.",
            ),
          ),
        ),
        el(
          "div",
          { class: "form", style: "max-width:none" },
          hintBox("on", `Show "Did you know?" hints to people in their first ${u.hints.days} days`),
          hintBox(
            "suggestAdmin",
            "Hints can suggest asking an admin for more (for example the pay and billing view)",
          ),
        ),
      ),
      // Levels and jobs (brief 1E-7, mockup v59).
      ...(jobsBody
        ? jobsCards(jobsBody, {
            canAdmin: u.canAdmin,
            viewAs: (r) => void startViewAs(r),
            reload: () => void load(),
          })
        : []),
      el(
        "section",
        { class: "card", "aria-label": "Jobs and what they start with" },
        el(
          "div",
          { class: "cardhead" },
          el("h2", {}, "Jobs and what they start with"),
          el(
            "span",
            { class: "small muted" },
            "None, View or Edit per area, optionally only their own",
          ),
        ),
        el(
          "div",
          { class: "tw" },
          el(
            "table",
            {},
            el(
              "thead",
              {},
              el(
                "tr",
                {},
                el("th", {}, "Role"),
                el("th", {}, "Sees"),
                el("th", { class: "num" }, "People"),
                el("th", { class: "num" }, ""),
              ),
            ),
            el("tbody", {}, ...roleRows),
          ),
        ),
      ),
      ...[signingCard()].filter((x): x is HTMLElement => x !== null),
    );
  }

  /** Add someone, or the pencil: the questions, This sets up, Fine-tune (mockup uinv). */
  function questions(person: Person | null) {
    const jobs = u.presets;
    let job: Job | "admin" | null = person?.admin
      ? "admin"
      : (jobs.find((j) => j.id === person?.setup.presetId) ??
        jobs.find((j) => j.name === "Office manager") ??
        jobs[0] ??
        null);
    if (!person) job = jobs.find((j) => j.name === "Scheduler") ?? jobs[0] ?? null;
    let answers: Answers =
      person && !person.admin && person.setup.presetId
        ? { ...person.setup.answers }
        : job && job !== "admin"
          ? { ...job.answers }
          : { money: true, payroll: true, release: true, scope: "everything" };
    let scopes = person ? [...person.setup.scopes] : [];
    // What was changed in Fine-tune, over the job and the answers.
    let fine: FineTune = { areas: {}, switches: {} };
    if (person?.setup.presetId && job && job !== "admin") {
      // The pencil opens with the person's own grid: what differs from the job is Fine-tune's.
      const base = rowsFor(presetOf(job), answers);
      for (const a of AREAS) {
        const b = base.find((r) => r.area === a);
        const was = b && b.level !== "none" ? `${b.level}${b.own_only ? " own" : ""}` : "off";
        const now = person.setup.grid[a] ?? "off";
        if (now !== was) fine.areas[a] = now;
      }
      for (const sw of u.switches.map((s) => s.value)) {
        const was = base.some((r) => r.switches[sw] === true);
        if ((person.setup.switches[sw] ?? false) !== was)
          fine.switches[sw] = person.setup.switches[sw];
      }
    }
    let naming = false;
    let fineOpen = false;

    const name = field("Name", person?.name ?? "", { maxlength: "120" });
    const email = field("Email or mobile", person?.email ?? "", {
      placeholder: "name@yourdomain.com",
      maxlength: "200",
    });
    if (person) email.input.disabled = true;
    const jobName = field("Name the job", "", { maxlength: "40" });
    // The Professional title row (brief 1E-5, mockup v57): what they can sign.
    const titleSel = el(
      "select",
      { "aria-label": "Professional title", disabled: !u.canSetTitle },
      ...u.titles.map((t) =>
        el(
          "option",
          { value: t ?? "", selected: (person?.title ?? null) === t },
          t ?? "None (office only)",
        ),
      ),
    );
    const titleRow = el(
      "div",
      { class: "qrow", "data-utitle": "1" },
      el("span", {}, "Professional title (what they can sign)"),
      titleSel,
    );
    const area = el("div");
    // Brief 1E-7: the person's jobs (more than one allowed) and one-off extras, each visible, and
    // whether an Admin owns the account.
    const jobsRow = el("div", { "data-person-jobs": "1" });
    function drawJobs() {
      const p = person && u.users.find((x) => x.id === person.id);
      if (!p) return jobsRow.replaceChildren();
      const parts: Node[] = [];
      const chips = el(
        "div",
        { style: "display:flex;gap:6px;flex-wrap:wrap;align-items:center" },
        ...p.jobs.map((j) =>
          el(
            "span",
            { class: "chip info" },
            j.name,
            j.added
              ? ib("x", `Take ${j.name} off ${p.name.split(" ")[0] ?? p.name}`, async () => {
                  const r = await api<JobsBody>(
                    "POST",
                    `/api/settings/users/${p.id}/jobs/${j.roleId}/remove`,
                  );
                  if (!r.ok) return toast("That wasn't saved", r.body.error);
                  toast(`${j.name} taken off`, "Takes effect on their next click");
                  await load();
                  drawJobs();
                })
              : null,
          ),
        ),
      );
      if (!p.admin) {
        const held = new Set(p.jobs.map((j) => j.presetId));
        const others = u.presets.filter((x) => !held.has(x.id));
        if (others.length) {
          const pick = el(
            "select",
            { "aria-label": "Give another job" },
            el("option", { value: "" }, "Give another job…"),
            ...others.map((x) => el("option", { value: x.id }, x.name)),
          );
          pick.style.maxWidth = "220px";
          const give = ib("plus", "Give this job too", async () => {
            if (!pick.value) return;
            const r = await api<JobsBody>("POST", `/api/settings/users/${p.id}/jobs`, {
              presetId: pick.value,
            });
            if (!r.ok) return toast("That wasn't saved", r.body.error);
            toast("Job given", "Access adds up; takes effect on their next click");
            await load();
            drawJobs();
          });
          chips.append(pick, give);
        }
      }
      parts.push(
        el("div", { class: "field" }, el("span", { class: "label" }, "Jobs"), chips),
        el(
          "p",
          { class: "small muted", style: "margin:0", "data-extras": "1" },
          p.extras.length ? `Extras: ${p.extras.join(", ")}` : "No one-off extras.",
        ),
      );
      if (p.admin) {
        const canOwn = jobsBody?.isOwner === true;
        parts.push(
          el(
            "div",
            { class: "qrow" },
            el("span", {}, "Account owner (billing, closing the account, transferring ownership)"),
            el(
              "div",
              { class: "seg", role: "group", "aria-label": "Account owner" },
              ...(
                [
                  [true, "Yes"],
                  [false, "No"],
                ] as const
              ).map(([v, l]) => {
                const b = el(
                  "button",
                  { type: "button", "aria-pressed": String(p.owner === v), disabled: !canOwn },
                  l,
                );
                b.addEventListener("click", async () => {
                  if (p.owner === v) return;
                  const r = await api<JobsBody>("PUT", `/api/settings/owners/${p.id}`, {
                    owner: v,
                  });
                  if (!r.ok) return toast("That wasn't saved", r.body.error);
                  toast(v ? `${p.name} owns the account` : `${p.name} stepped down as owner`, "");
                  await load();
                  drawJobs();
                });
                return b;
              }),
            ),
          ),
        );
        if (!canOwn)
          parts.push(
            el(
              "p",
              { class: "small muted", style: "margin:0" },
              "Only an account owner can change who owns the account.",
            ),
          );
      }
      jobsRow.replaceChildren(...parts);
    }
    drawJobs();
    const save = el(
      "button",
      { type: "button", class: "btn pri" },
      icon("check"),
      person ? " Save" : " Send invite",
    );
    const cancel = el("button", { type: "button", class: "btn" }, "Cancel");
    const m = openModal({
      title: person ? person.name : "Add someone",
      sub: "Answer a few questions; FlowCare sets up what they see. Change anything later.",
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          el("div", { class: "grid2", style: "gap:8px" }, name.el, email.el),
          jobsRow,
          area,
        ),
      ],
      actions: [save, cancel],
    });
    cancel.addEventListener("click", () => m.close());

    const yn = (label: string, on: boolean, pick: (v: boolean) => void) =>
      el(
        "div",
        { class: "seg", role: "group", "aria-label": label },
        ...(
          [
            [true, "Yes"],
            [false, "No"],
          ] as const
        ).map(([v, l]) => {
          const b = el("button", { type: "button", "aria-pressed": String(on === v) }, l);
          b.addEventListener("click", () => pick(v));
          return b;
        }),
      );

    const rows = () => (job && job !== "admin" ? rowsFor(presetOf(job), answers, fine) : []);

    function drawSheet() {
      const isField = job !== "admin" && !!job?.field;
      const chips = el(
        "div",
        { class: "jobchips", role: "group", "aria-label": "What do they do?" },
        ...(u.canAdmin ? [["admin", "Admin"] as const] : []).map(([v, l]) => {
          const b = el(
            "button",
            {
              type: "button",
              class: `chip ${job === v ? "info" : "plain"}`,
              "aria-pressed": String(job === v),
            },
            l,
          );
          b.addEventListener("click", () => {
            job = "admin";
            naming = false;
            drawSheet();
          });
          return b;
        }),
        ...jobs.map((j) => {
          const on = job !== "admin" && job?.id === j.id && !naming;
          const b = el(
            "button",
            { type: "button", class: `chip ${on ? "info" : "plain"}`, "aria-pressed": String(on) },
            j.name,
          );
          b.addEventListener("click", () => {
            job = j;
            answers = { ...j.answers };
            fine = { areas: {}, switches: {} };
            scopes = [];
            naming = false;
            drawSheet();
          });
          return b;
        }),
        (() => {
          const b = el(
            "button",
            {
              type: "button",
              class: `chip ${naming ? "info" : "plain"}`,
              "aria-pressed": String(naming),
              "data-tip": "It starts from the closest one, with a new name",
            },
            "Something else",
          );
          b.addEventListener("click", () => {
            if (job === "admin" || !job) job = jobs[0] ?? null;
            naming = true;
            drawSheet();
            jobName.input.focus();
          });
          return b;
        })(),
      );
      const parts: Node[] = [
        el("div", { class: "field" }, el("span", { class: "label" }, "What do they do?"), chips),
      ];
      if (naming && job && job !== "admin")
        parts.push(
          jobName.el,
          el(
            "p",
            { class: "small muted", style: "margin:0" },
            `Starts from ${job.name}. It's saved as a job for next time.`,
          ),
        );
      if (job === "admin") {
        parts.push(
          titleRow,
          el(
            "p",
            { class: "small", style: "margin:10px 0 4px" },
            el("b", {}, "This sets up: "),
            "Everything, every module, every setting.",
          ),
          el(
            "p",
            { class: "small muted", style: "margin:0" },
            "An office user: counted on the plan.",
          ),
        );
        area.replaceChildren(...parts);
        return;
      }
      if (!job) {
        area.replaceChildren(...parts);
        return;
      }
      if (!isField)
        for (const q of u.questions)
          parts.push(
            el(
              "div",
              { class: "qrow" },
              el("span", {}, q.words),
              yn(q.words, answers[q.key], (v) => {
                answers = { ...answers, [q.key]: v };
                // The answer wins over an earlier Fine-tune of the same switch.
                const sw = QUESTION_SWITCH[q.key];
                fine = {
                  ...fine,
                  switches: Object.fromEntries(
                    Object.entries(fine.switches).filter(([k]) => k !== sw),
                  ),
                };
                drawSheet();
              }),
            ),
          );
      const which = el(
        "select",
        { "aria-label": "Which patients and clients" },
        ...u.scopeAnswers.map((a) =>
          el("option", { value: a.value, selected: a.value === answers.scope }, a.label),
        ),
      );
      which.addEventListener("change", () => {
        answers = { ...answers, scope: which.value as ScopeAnswer };
        scopes = [];
        drawSheet();
      });
      parts.push(
        el("div", { class: "qrow" }, el("span", {}, "Which patients and clients?"), which),
      );
      if (answers.scope === "counties" || answers.scope === "clients") parts.push(scopeChips());
      const r = rows();
      const kind = userKind(effectiveAccess([{ name: "x", built_in: false, permissions: r }]));
      parts.push(
        titleRow,
        el(
          "p",
          { class: "small", style: "margin:10px 0 4px", "aria-live": "polite" },
          el("b", {}, "This sets up: "),
          setsUp(
            r,
            isField,
            u.areas.map((a) => a.value),
          ),
        ),
        el(
          "p",
          { class: "small muted", style: "margin:0" },
          kind === "field" ? "A field user: free." : "An office user: counted on the plan.",
        ),
      );
      if (!isField) parts.push(fineTune(r));
      area.replaceChildren(...parts);
    }

    function scopeChips(): HTMLElement {
      const counties = answers.scope === "counties";
      const label = (s: { kind: string; value: string }) =>
        s.kind === "client"
          ? (u.clients.find((c) => c.id === s.value)?.name ?? "Client")
          : s.kind === "client_type"
            ? (u.clientTypes.find((c) => c.id === s.value)?.name ?? "Client type")
            : s.value;
      const box = el("div", {
        style: "display:flex;gap:6px;flex-wrap:wrap;margin-top:6px;align-items:center",
      });
      for (const s of scopes)
        box.append(
          el(
            "span",
            { class: "chip info" },
            label(s),
            ib("x", `Take off ${label(s)}`, () => {
              scopes = scopes.filter((x) => x !== s);
              drawSheet();
            }),
          ),
        );
      const options: [string, string][] = counties
        ? u.counties
            .filter((c) => !scopes.some((s) => s.value === c))
            .map((c) => [`county:${c}`, c])
        : [
            ...u.clients.map((c) => [`client:${c.id}`, c.name] as [string, string]),
            ...u.clientTypes.map(
              (t) => [`client_type:${t.id}`, `${t.name} (client type)`] as [string, string],
            ),
          ].filter(([k]) => !scopes.some((s) => `${s.kind}:${s.value}` === k));
      const pick = el(
        "select",
        { "aria-label": counties ? "County" : "Client or client type" },
        el("option", { value: "" }, counties ? "Pick a county" : "Pick a client"),
        ...options.map(([v, l]) => el("option", { value: v }, l)),
      );
      const addIt = ib("plus", counties ? "Add a county" : "Add a client", () => {
        const v = pick.value;
        if (!v) return;
        const i = v.indexOf(":");
        scopes = [...scopes, { kind: v.slice(0, i), value: v.slice(i + 1) }];
        drawSheet();
      });
      pick.style.maxWidth = "220px";
      box.append(pick, addIt);
      return box;
    }

    function fineTune(r: ReturnType<typeof rows>): HTMLElement {
      const level = (a: Area): Spec => {
        const x = r.find((y) => y.area === a);
        return !x || x.level === "none" ? "off" : (`${x.level}${x.own_only ? " own" : ""}` as Spec);
      };
      const d = el("details", { class: "fine", open: fineOpen });
      d.append(
        el("summary", {}, "Fine-tune"),
        el(
          "div",
          { class: "ftg" },
          ...u.areas.flatMap((a) => {
            const now = level(a.value);
            const base = now === "off" ? "off" : now.split(" ")[0];
            const own = now.endsWith(" own");
            return [
              el(
                "span",
                {},
                a.label,
                own ? el("span", { class: "small muted" }, " (only their own)") : "",
              ),
              el(
                "div",
                { class: "seg", role: "group", "aria-label": a.label },
                ...(
                  [
                    ["off", "Off"],
                    ["view", "View"],
                    ["edit", "Edit"],
                  ] as const
                ).map(([v, l]) => {
                  const b = el("button", { type: "button", "aria-pressed": String(base === v) }, l);
                  b.addEventListener("click", () => {
                    fine.areas[a.value] = v === "off" ? "off" : ((own ? `${v} own` : v) as Spec);
                    drawSheet();
                  });
                  return b;
                }),
              ),
            ];
          }),
        ),
        el(
          "div",
          { style: "margin-top:8px" },
          ...u.switches.map((sw) => {
            const i = el("input", {
              type: "checkbox",
              checked: r.some((x) => x.switches[sw.value] === true),
            });
            i.addEventListener("change", () => {
              fine.switches[sw.value] = i.checked;
              drawSheet();
            });
            return el("label", { class: "ptask" }, i, ` ${sw.label}`);
          }),
        ),
      );
      d.addEventListener("toggle", () => (fineOpen = d.open));
      return d;
    }

    save.addEventListener("click", async () => {
      if (!job) return refuse(m.note, "Pick what they do.");
      save.disabled = true;
      let presetId = job === "admin" ? null : job.id;
      if (naming && job !== "admin") {
        const named = await api<UsersBody>("POST", "/api/settings/presets", {
          name: jobName.input.value,
          from: job.id,
          answers,
          fine,
        });
        if (!named.ok || !named.body.preset) {
          save.disabled = false;
          return refuse(m.note, named.body.error);
        }
        u = named.body;
        presetId = named.body.preset.id;
        fine = { areas: {}, switches: {} };
      }
      const payload = {
        name: name.input.value,
        ...(person ? {} : { email: email.input.value }),
        ...(job === "admin" ? { job: "admin" } : { presetId, answers, fine, scopes }),
      };
      const res = person
        ? await api<UsersBody>("PUT", `/api/settings/users/${person.id}`, payload)
        : await api<UsersBody>("POST", "/api/settings/users", payload);
      save.disabled = false;
      if (!res.ok) return refuse(m.note, REFUSED[res.status] ?? res.body.error);
      // Their professional title, when it changed (Settings › Users at Edit).
      const title = titleSel.value || null;
      const who =
        person?.id ??
        res.body.users?.find((x) => x.email === email.input.value.trim().toLowerCase())?.id;
      if (u.canSetTitle && who && title !== (person?.title ?? null)) {
        const t = await api("PUT", `/api/settings/users/${who}/title`, { title });
        if (!t.ok) return refuse(m.note, (t.body as { error?: string }).error);
      }
      m.close();
      u = res.body;
      toast(
        person ? "Saved" : "Invite sent",
        person ? "Takes effect on their next click" : "They get a link to sign in",
      );
      await load();
      if (person?.id === ctx.me.user.id) await ctx.refresh();
    });

    drawSheet();
    if (!person) name.input.focus();
  }

  function editRole(role: Role) {
    const row = (area: Area) => role.permissions.find((p) => p.area === area);
    const inputs = AREAS.map((area) => {
      const p = row(area);
      const level = p?.level ?? "none";
      const radios = (["none", "view", "edit"] as const).map((l) =>
        el("input", {
          type: "radio",
          name: `lvl-${area}`,
          value: l,
          checked: level === l,
          "aria-label": `${AREA_LABEL[area]}: ${l}`,
        }),
      );
      const own = el("input", {
        type: "checkbox",
        checked: p?.own_only ?? false,
        "aria-label": `${AREA_LABEL[area]}: only their own`,
      });
      const switches = (Object.keys(SWITCH_AREA) as Switch[])
        .filter((s) => SWITCH_AREA[s] === area)
        .map((s) => ({
          s,
          input: el("input", {
            type: "checkbox",
            checked: p?.switches[s] === true,
            "aria-label": SWITCH_LABEL[s],
          }),
        }));
      return { area, radios, own, switches };
    });
    const table = el(
      "div",
      { class: "tw" },
      el(
        "table",
        { class: "mx" },
        el(
          "thead",
          {},
          el(
            "tr",
            {},
            el("th", {}, "Area"),
            el("th", {}, "None"),
            el("th", {}, "View"),
            el("th", {}, "Edit"),
            el("th", {}, "Only their own"),
            el("th", {}, "Switches"),
          ),
        ),
        el(
          "tbody",
          {},
          ...inputs.map((i) =>
            el(
              "tr",
              { "data-area": i.area },
              el("td", {}, el("b", {}, AREA_LABEL[i.area])),
              ...i.radios.map((r) => el("td", {}, r)),
              el("td", {}, i.own),
              el(
                "td",
                { style: "text-align:left" },
                ...i.switches.map((s) =>
                  el("label", { class: "swl" }, s.input, ` ${SWITCH_LABEL[s.s]}`),
                ),
              ),
            ),
          ),
        ),
      ),
    );
    const save = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save");
    // The role's history, in the activity panel inside the modal (so focus can reach it).
    const activity = activityPanel("role", role.id);
    const { close, note } = openModal({
      title: `Edit ${role.name}`,
      body: [table, activity.el],
      actions: [save],
      wide: true,
    });
    save.addEventListener("click", async () => {
      const permissions = inputs.map((i) => {
        const level = (i.radios.find((r) => r.checked)?.value ?? "none") as Perm["level"];
        return {
          area: i.area,
          level,
          own_only: level !== "none" && i.own.checked,
          switches: Object.fromEntries(i.switches.map((s) => [s.s, s.input.checked])),
        };
      });
      save.disabled = true;
      const res = await api<{ roles: Role[] }>(
        "PUT",
        `/api/settings/roles/${role.id}/permissions`,
        { permissions },
      );
      save.disabled = false;
      if (!res.ok) {
        note.hidden = false;
        note.textContent = REFUSED[res.status] ?? res.body.error ?? "That change wasn't saved.";
        return;
      }
      close();
      toast(`${role.name} saved`, "Everyone with this role sees the change on their next click");
      await load();
      await ctx.refresh();
    });
  }

  await load();
}
